import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon,
  TruckIcon,
  UserIcon,
  PlusCircleIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

function Dashboard() {
  const { currentUser } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalBookings: 0,
    confirmedBookings: 0,
    pendingBookings: 0,
    upcomingRides: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_URLS.bookings.rider);
      const allBookings = response.data;
      
      // Calculate stats
      const totalBookings = allBookings.length;
      const confirmedBookings = allBookings.filter(b => b.status === 'confirmed' || b.status === 'accepted').length;
      const pendingBookings = allBookings.filter(b => b.status === 'pending').length;
      
      // Get upcoming bookings (today and future)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcoming = allBookings.filter(booking => {
        const bookingDate = new Date(booking.date || booking.Ride?.date);
        return bookingDate >= today && (booking.status === 'confirmed' || booking.status === 'accepted');
      }).sort((a, b) => {
        const dateA = new Date(a.date || a.Ride?.date);
        const dateB = new Date(b.date || b.Ride?.date);
        return dateA - dateB;
      });

      setBookings(allBookings);
      setUpcomingBookings(upcoming.slice(0, 3)); // Show only next 3 upcoming bookings
      setStats({
        totalBookings,
        confirmedBookings,
        pendingBookings,
        upcomingRides: upcoming.length
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      accepted: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      declined: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    const icons = {
      confirmed: CheckCircleIcon,
      accepted: CheckCircleIcon,
      pending: ExclamationTriangleIcon,
      declined: XCircleIcon,
      cancelled: XCircleIcon
    };
    
    const Icon = icons[status] || ExclamationTriangleIcon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const time = new Date(`2000-01-01 ${timeString}`);
      return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome, {currentUser?.firstName || "Rider"}!
        </h1>
        <p className="text-gray-600">
          This is your rider dashboard. You can view and manage your rides here.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {[
          {
            title: 'Total Bookings',
            value: stats.totalBookings,
            icon: CalendarIcon,
            color: 'bg-blue-500'
          },
          {
            title: 'Confirmed Rides',
            value: stats.confirmedBookings,
            icon: CheckCircleIcon,
            color: 'bg-green-500'
          },
          {
            title: 'Pending Requests',
            value: stats.pendingBookings,
            icon: ExclamationTriangleIcon,
            color: 'bg-yellow-500'
          },
          {
            title: 'Upcoming Rides',
            value: stats.upcomingRides,
            icon: ClockIcon,
            color: 'bg-purple-500'
          }
        ].map((stat, index) => (
          <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`${stat.color} rounded-md p-3`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.title}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {loading ? '...' : stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upcoming Bookings */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg leading-6 font-medium text-gray-900">Upcoming Bookings</h2>
                  <p className="mt-1 text-sm text-gray-500">Your confirmed rides for the coming days</p>
                </div>
                <Link 
                  to="/rider/my-bookings" 
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View All
                </Link>
              </div>
            </div>
            
            {loading ? (
              <div className="px-4 py-5 sm:p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading your bookings...</p>
              </div>
            ) : upcomingBookings.length === 0 ? (
              <div className="px-4 py-5 sm:p-6 text-center">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming bookings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  You don't have any confirmed rides scheduled. Book a ride to get started!
                </p>
                <div className="mt-6">
                  <Link
                    to="/rider/available-rides"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <PlusCircleIcon className="h-5 w-5 mr-2" />
                    Browse Available Rides
                  </Link>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {upcomingBookings.map((booking) => (
                  <div key={booking.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <MapPinIcon className="h-6 w-6 text-indigo-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {/* Show pickup location - handle both direct bookings and ride-based bookings */}
                            {booking.pickupStreet && booking.pickupCity 
                              ? `${booking.pickupStreet}, ${booking.pickupCity} ${booking.pickupPostcode}` 
                              : booking.pickupLocation || booking.Ride?.pickupLocation || 'Pickup location'
                            } → Church
                          </div>
                          <div className="text-sm text-gray-500">
                            {booking.Ride?.driver && (
                              <>Driver: {booking.Ride.driver.firstName} {booking.Ride.driver.lastName}</>
                            )}
                            {booking.driver && (
                              <>Driver: {booking.driver.firstName} {booking.driver.lastName}</>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {formatDate(booking.date || booking.Ride?.date)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatTime(booking.serviceTime || booking.Ride?.time)}
                          </div>
                        </div>
                        {getStatusBadge(booking.status)}
                      </div>
                    </div>
                    {booking.serviceSession && (
                      <div className="mt-2 ml-14">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {booking.serviceSession} Service
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <Link
            to="/rider/available-rides"
            className="block p-6 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-indigo-500" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Browse Available Rides</h3>
                <p className="mt-1 text-sm text-gray-500">
                  View and request available rides to church
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/rider/book-ride"
            className="block p-6 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <PlusCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Book a Ride</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Request a new ride to church
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/rider/my-bookings"
            className="block p-6 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">My Bookings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  View and manage all your ride bookings
                </p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
